import React, { useState } from "react";
import { showToast } from "../../../utils/toast";
import { formatPhoneNumber, getPhoneNumberError } from "../../../utils/common";
import {
  ModalField,
  ModalFields,
  ModalFile,
  ModalForm,
  ModalGroup,
  ModalInput,
} from "../../../design-system/patterns/modal-form";

/**
 * Seller profile edit. One of the nine modal forms.
 *
 * The 5MB file guard and the live phone formatter are unchanged.
 *
 * Two dead things went with the CSS. The commented-out first name and last name
 * fields, which the API does not take from this form, and the inline
 * `style={{ color: "#ef4444" }}` on the phone error, which was a hardcoded hex
 * doing the job `ModalField`'s error slot does properly.
 *
 * No address fields. They used to post pre-filled on every save and overwrite
 * the default address, so changing a name could move the store's collection
 * point. The store address has its own form, `EditDeliveryForm`.
 */

const EditProfileForm = ({ open = true, initial, onSubmit, onCancel, submitting }) => {
  const [formState, setFormState] = useState({
    full_name: initial?.full_name || "",
    first_name: initial?.first_name || "",
    last_name: initial?.last_name || "",
    store_name: initial?.store_name || "",
    phone_number: initial?.phone_number || "",
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Format phone number as user types
    if (name === "phone_number") {
      processedValue = formatPhoneNumber(value, formState.phone_number);

      // Clear phone error when user starts typing
      if (errors.phone_number) {
        setErrors((prev) => ({ ...prev, phone_number: null }));
      }
    }

    setFormState((s) => ({ ...s, [name]: processedValue }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const maxSize = 5 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        showToast.error("File size must be less than 5MB");
        e.target.value = "";
        return;
      }
      setFile(selectedFile);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formState.phone_number) {
      const phoneError = getPhoneNumberError(formState.phone_number);
      if (phoneError) {
        newErrors.phone_number = phoneError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      showToast.error("Please fix the errors before submitting");
      return;
    }

    const fd = new FormData();
    Object.entries(formState).forEach(([k, v]) => fd.append(k, v ?? ""));
    if (file) fd.append("profile_image", file);
    onSubmit?.(fd);
  };

  return (
    <ModalForm
      open={open}
      title="Edit profile"
      onClose={onCancel}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Save changes"
      submittingLabel="Saving"
    >
      <ModalGroup title="Who you are">
        <ModalFields cols={2}>
          <ModalField label="Full name">
            <ModalInput
              name="full_name"
              value={formState.full_name}
              onChange={handleChange}
              autoComplete="name"
              disabled={submitting}
            />
          </ModalField>

          <ModalField label="Store name">
            <ModalInput
              name="store_name"
              value={formState.store_name}
              onChange={handleChange}
              disabled={submitting}
            />
          </ModalField>

          <ModalField label="Phone" error={errors.phone_number}>
            <ModalInput
              name="phone_number"
              type="tel"
              value={formState.phone_number}
              onChange={handleChange}
              placeholder="071 234 5678"
              autoComplete="tel"
              disabled={submitting}
            />
          </ModalField>

          <ModalField label="Profile image" optional>
            <ModalFile
              accept="image/*"
              onChange={handleFileChange}
              disabled={submitting}
              note="JPG or PNG, up to 5MB."
            />
          </ModalField>
        </ModalFields>
      </ModalGroup>

    </ModalForm>
  );
};

export default EditProfileForm;
